import { useEffect, useMemo, useRef, useState } from 'react';

import AddBoxIcon from '@mui/icons-material/AddBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, Grid, IconButton, InputBase, Stack, Typography } from '@mui/material';
import { convertsCentsToCurrency, isEmpty } from '@npi/buddy-utils';
import dayjs, { Dayjs } from 'dayjs';
import { Form, Formik, isNaN } from 'formik';
import { CreateSalesInvoiceInput } from 'types/graphql';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useQuery } from '@redwoodjs/web';

import { Spacer, ThumbnailCard } from 'src/components';
import { InputBaseField } from 'src/components/common/form/FormInput';
import { RadioGroupField } from 'src/components/common/form/FormRadioGroup';
import { SelectBase, SelectBaseField } from 'src/components/common/form/FormSelect';
import { Flex } from 'src/components/Flex';
import { NPIAutoComplete } from 'src/components/FormikComponents';
import { AutoCompleteOptionType } from 'src/components/FormikComponents/NPIAutoComplete';
import { NPICurrencyInput } from 'src/components/inputs/NPICurrencyInput';
import MainCard from 'src/components/MainCard/MainCard';
import NPISingleDatePicker from 'src/components/NPISingleDatePicker';
import { InvoiceTo } from 'src/constants/merchants';
import useGetLedgerAccounts from 'src/features/finance/hooks/useGetLedgerAccounts';
import { GET_MERCHANTS } from 'src/graphql/queries/merchants.query';
import { GET_PARTNERS } from 'src/graphql/queries/partners.query';
import { useCreateSalesInvoice } from 'src/hooks';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import { getVatAmount } from 'src/lib/countryConfig';
import { addDaysToDate, onError, sequenceGenerator } from 'src/utils/utils';
import { addSalesInvoiceValidation } from 'src/validation/SalesInvoice';

import { BillingInformation } from './components/BillingInformation';
import MerchantSelectFields from './components/MerchantSelectFields';

interface InvoiceItem {
    ledgerAccount: string;
    description: string;
    quantity: number;
    netAmount: number;
    totalNet: number;
    totalVat: number;
    lineTotal: number;
}

interface InvoiceItemType {
    [key: number]: InvoiceItem;
}

const defaultInvoiceItem: InvoiceItem = {
    ledgerAccount: '',
    description: '',
    quantity: 1,
    netAmount: 0,
    totalNet: 0,
    totalVat: 0,
    lineTotal: 0,
};

interface InvoiceAccumulator {
    totalNet: number;
    totalVat: number;
    totalAmount: number;
}

enum BillingCycle {
    RECURRING = 'RECURRING',
    ONE_OFF = 'ONE_OFF',
}

enum OccurrenceType {
    FIXED_OCCURRENCE = 'FIXED_OCCURRENCE',
    CREATE_UNTIL_CANCEL = 'CREATE_UNTIL_CANCEL',
}

const AddSalesInvoicePage = () => {
    const timeout = useRef(null);
    const today = dayjs();
    const [invoiceDate, setInvoiceDate] = useState(today);
    const [recurringStartDate, setRecurringStartDate] = useState(today);
    const [recurringStartDateConfig, setRecurringStartDateConfig] = useState({
        occurrence: '',
        start: 0,
    });
    const [invoiceTo, setInvoiceTo] = useState(InvoiceTo.PARTNER);
    const [invoiceItems, setInvoiceItems] = useState<InvoiceItemType>({
        0: defaultInvoiceItem,
    });
    const [errorMessage, setErrorMessage] = useState({
        invoiceItems: '',
        index: undefined,
    });
    const [showRecurringInvoiceConfig, setShowRecurringInvoiceConfig] = useState(false);
    const [invoiceFrequencies, setInvoiceFrequencies] = useState<number[]>([]);
    const [showNumberOfInvoices, setShowNumberOfInvoices] = useState(false);

    const { data: partnersData } = useQuery(GET_PARTNERS, {
        onError,
    });

    const { data: merchantsData } = useQuery(GET_MERCHANTS, {
        onError,
    });
    const { ledgerAccounts } = useGetLedgerAccounts();
    const { handleCreateSalesInvoice, isLoading } = useCreateSalesInvoice();

    useEffect(() => {
        return () => {
            return clearTimeout(timeout.current);
        };
    }, [timeout]);

    const invoiceFrequencyOptions = useMemo<AutoCompleteOptionType[]>(() => {
        return invoiceFrequencies.length > 0
            ? invoiceFrequencies.map((num) => {
                  return {
                      id: num,
                      label: `${num}`,
                      value: `${num}`,
                  };
              })
            : [];
    }, [invoiceFrequencies]);

    const partnerOptions = useMemo<AutoCompleteOptionType[]>(() => {
        return (partnersData?.partners ?? [])
            .map(({ id, business }) => {
                return {
                    id,
                    label: business?.legalName,
                    value: business?.legalName,
                };
            })
            .filter(({ value }) => {
                return !isEmpty(value);
            });
    }, [partnersData]);

    const merchantOptions = useMemo<AutoCompleteOptionType[]>(() => {
        return (merchantsData?.merchants?.data ?? []).map(
            ({ id, merchantName, tradingName, merchantContactName }) => {
                return {
                    label: merchantName ?? tradingName ?? merchantContactName,
                    value: id,
                };
            }
        );
    }, [merchantsData]);

    const merchantAccountNumberOptions = useMemo<AutoCompleteOptionType[]>(() => {
        return (merchantsData?.merchants?.data ?? []).map(({ id, merchantAccountNumber }) => {
            return {
                label: merchantAccountNumber,
                value: id,
            };
        });
    }, [merchantsData]);

    const updatedInvoiceItems = useMemo(() => {
        return Object.values(invoiceItems).map((item) => {
            return {
                ...item,
                lineTotal: Math.round(item.lineTotal),
                totalVat: Math.round(item.totalVat),
            };
        });
    }, [invoiceItems]);

    const updatedTotalValues = useMemo(() => {
        return updatedInvoiceItems.reduce(
            (accumulator: InvoiceAccumulator, item: InvoiceItem) => {
                accumulator.totalNet += item.totalNet;
                accumulator.totalVat += Math.round(item.totalVat);
                accumulator.totalAmount += Math.round(item.lineTotal);
                return accumulator;
            },
            { totalNet: 0, totalVat: 0, totalAmount: 0 }
        );
    }, [updatedInvoiceItems]);

    const onSubmit = async (input) => {
        if (updatedTotalValues.totalAmount === 0) {
            setErrorMessage((error) => {
                return {
                    ...error,
                    invoiceItems: 'Invoice required atleast one item',
                    index: 0,
                };
            });
            return;
        }

        const validatedItemFields = ['ledgerAccount', 'description', 'quantity', 'netAmount'];

        let hasInvalidItemFields = false;
        updatedInvoiceItems.forEach((item, index) => {
            Object.entries(item).forEach(([key, value]) => {
                if (validatedItemFields.includes(key) && !value) {
                    setErrorMessage((error) => {
                        return {
                            ...error,
                            invoiceItems: `Invalid item field(s)`,
                            index,
                        };
                    });
                    hasInvalidItemFields = true;
                    return;
                }
            });
        });

        if (hasInvalidItemFields) return;

        const formattedInvoiceDate = invoiceDate.toDate();

        const formattedInvoiceDueDate = formattedInvoiceDate
            ? addDaysToDate({ date: formattedInvoiceDate, days: 7 })
            : addDaysToDate({ date: new Date(), days: 7 });

        const formattedRecurringStartDate =
            input.billingCycle === BillingCycle.RECURRING ? recurringStartDate.toDate() : null;

        const {
            invoiceTo,
            partner,
            merchant,
            merchantAccountNumber,
            billingCycle,
            occurrenceType,
        } = input;
        let data = {};
        if (invoiceTo === InvoiceTo.PARTNER) {
            data = { ...data, partnerId: partner.id };
        }

        if (invoiceTo === InvoiceTo.MERCHANT) {
            data = { ...data, merchantId: merchant.value ?? merchantAccountNumber.value };
        }

        if (billingCycle === BillingCycle.RECURRING) {
            data = {
                ...data,
                occurrence: input.invoiceOccurrence,
                frequency: (input.invoiceFrequency as AutoCompleteOptionType).id,
                occurrenceType: input.occurrenceType,
                recurringStartDate: formattedRecurringStartDate,
            };
        }

        if (
            billingCycle === BillingCycle.RECURRING &&
            occurrenceType === OccurrenceType.FIXED_OCCURRENCE
        ) {
            data = {
                ...data,
                numberOfInvoices: input.numberOfInvoices,
            };
        }

        const { totalNet, totalVat, totalAmount } = updatedTotalValues;
        const inputData = {
            ...data,
            to: invoiceTo,
            salesInvoiceDate: formattedInvoiceDate,
            salesInvoiceDueDate: formattedInvoiceDueDate,
            billingCycle,
            invoiceItems: updatedInvoiceItems,
            totalNetAmount: totalNet,
            totalVatAmount: totalVat,
            totalInvoiceAmount: totalAmount,
            totalOutstanding: totalAmount,
            numberOfInvoices: input.numberOfInvoices,
        };

        await handleCreateSalesInvoice(inputData as CreateSalesInvoiceInput);
    };

    const handleInvoiceDate = (newDate: Dayjs) => {
        setInvoiceDate(newDate);
    };

    const handleRecurringStartDate = (newDate: Dayjs) => {
        setRecurringStartDate(newDate);
    };

    const handleOnChangeBillingCycle = (value: string) => {
        if (value === BillingCycle.RECURRING) {
            setShowRecurringInvoiceConfig(true);
        } else {
            setShowRecurringInvoiceConfig(false);
        }
    };

    const handleOnChangeInvoiceOccurrence = (value: string) => {
        const mapInvoiceFrequencies = {
            DAILY: { start: 7, stop: 365, step: 1 },
            WEEKLY: { start: 1, stop: 52, step: 1 },
            MONTHLY: { start: 1, stop: 12, step: 1 },
        };
        const config = mapInvoiceFrequencies[value];
        const generatedFrequencies = sequenceGenerator(config);
        setInvoiceFrequencies(generatedFrequencies);
        setRecurringStartDateConfig((config) => {
            return { ...config, occurrence: value };
        });
    };

    const handleOnchangeInvoiceFrequency = (frequency: AutoCompleteOptionType) => {
        const mapDayjsOccurence = {
            DAILY: 'd',
            WEEKLY: 'w',
            MONTHLY: 'M',
        };
        const occurrence = mapDayjsOccurence[recurringStartDateConfig.occurrence];
        const startDate = dayjs(invoiceDate).add(frequency.id, occurrence);
        setRecurringStartDate(startDate);
    };

    const handleOnChangeOccurenceType = (value: string) => {
        if (value === OccurrenceType.FIXED_OCCURRENCE) {
            setShowNumberOfInvoices(true);
        } else {
            setShowNumberOfInvoices(false);
        }
    };

    const handleOnchangeInvoiceItem = (e: any) => {
        if (e?.nativeEvent) {
            e.preventDefault();
        }
        const { name: inputName, value } = e.target;
        setErrorMessage({ invoiceItems: '', index: undefined });

        const [name, index] = inputName.split('-');

        const numberFields = ['quantity', 'netAmount'];
        const isUpdatingNumberField = numberFields.includes(name);
        let updatedValue = isUpdatingNumberField ? parseFloat(value) : value;
        if (isUpdatingNumberField) {
            updatedValue = isNaN(updatedValue) ? 0 : updatedValue;
        }
        const item = invoiceItems[index];
        const invoiceItem = { ...item, [name]: updatedValue };

        setInvoiceItems((prev) => {
            const netAmountInCents = invoiceItem?.netAmount;

            const totalNetInCents = invoiceItem.quantity * netAmountInCents;
            const totalVatInCents = Math.round(totalNetInCents * getVatAmount());
            const lineTotalInCents = totalNetInCents + totalVatInCents;

            return {
                ...prev,
                [index]: {
                    ...item,
                    [name]: updatedValue,
                    totalNet: totalNetInCents,
                    totalVat: totalVatInCents,
                    lineTotal: lineTotalInCents,
                },
            };
        });
    };

    const handleAddInvoiceItem = () => {
        const newItemIndex = Object.keys(invoiceItems).length;
        setInvoiceItems((prev) => {
            return { ...prev, [newItemIndex]: defaultInvoiceItem };
        });
    };

    const handleRemoveInvoiceItem = () => {
        const itemKeys = Object.keys(invoiceItems).slice(0, -1);
        const removedItem: InvoiceItemType = Object.fromEntries(
            itemKeys.map((itemKey) => {
                return [itemKey, invoiceItems[itemKey]];
            })
        );
        setInvoiceItems(removedItem);
        setErrorMessage({ invoiceItems: '', index: undefined });
    };

    const selectedMerchant = (selectedMerchantId: number) => {
        return (merchantsData?.merchants?.data ?? []).find((merchant) => {
            return merchant.id === selectedMerchantId;
        });
    };

    const handleNetAmount = (name, index, valueInCents, invoiceItems) => {
        const parsedValue = Number.isFinite(valueInCents) ? valueInCents : 0;
        const currentNetAmount = invoiceItems[index][name];
        if (currentNetAmount !== undefined && currentNetAmount !== parsedValue) {
            const item = invoiceItems[index];
            const invoiceItem = { ...item, [name]: parsedValue };

            setInvoiceItems((prev) => {
                const totalNetInCents = invoiceItem.quantity * parsedValue;
                const totalVatInCents = Math.round(totalNetInCents * getVatAmount());
                const lineTotalInCents = totalNetInCents + totalVatInCents;

                return {
                    ...prev,
                    [index]: {
                        ...item,
                        [name]: parsedValue,
                        totalNet: totalNetInCents,
                        totalVat: totalVatInCents,
                        lineTotal: lineTotalInCents,
                    },
                };
            });
        }
    };

    const initialValues = {
        invoiceTo: InvoiceTo.MERCHANT,
        partner: undefined,
        merchant: undefined,
        billingCycle: null,
        invoiceOccurrence: null,
        invoiceFrequency: undefined,
        occurrenceType: null,
        numberOfInvoices: 0,
        comments: '',
        merchantAccountNumber: null,
    };

    return (
        <PageContent heading="Add Sales Invoice">
            <MetaTags title="Sales Invoice" description="Add Sales Invoice" />
            <Formik
                initialValues={initialValues}
                validationSchema={addSalesInvoiceValidation}
                onSubmit={onSubmit}
            >
                {({ setFieldValue, values, isValid, isSubmitting, dirty }) => {
                    return (
                        <Form>
                            <Stack spacing={5}>
                                <Box>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={5}>
                                            <MainCard>
                                                <Stack>
                                                    <h2 className="font-normal mb-0 mt-10">
                                                        Invoice Details
                                                    </h2>
                                                    <p>Enter all details below</p>
                                                    <Box>
                                                        <Flex flexDirection={'column'} gap={0.5}>
                                                            <Typography
                                                                fontWeight={600}
                                                                fontSize="14px"
                                                                lineHeight="21px"
                                                            >
                                                                Date
                                                            </Typography>
                                                            <NPISingleDatePicker
                                                                value={invoiceDate}
                                                                onChange={handleInvoiceDate}
                                                                maxDate={today}
                                                            />
                                                        </Flex>
                                                    </Box>
                                                    <Box className="mt-10">
                                                        <RadioGroupField
                                                            label="Invoice To"
                                                            containerGridSpacing={3}
                                                            labelGridSize={3}
                                                            radioGroupGridSize={6}
                                                            radioOptionGridSize={6}
                                                            setselectedvalue={setInvoiceTo}
                                                            name="invoiceTo"
                                                            options={[
                                                                {
                                                                    label: 'Merchant',
                                                                    value: 'MERCHANT',
                                                                },
                                                            ]}
                                                        />
                                                    </Box>
                                                    <Box>
                                                        {invoiceTo === InvoiceTo.PARTNER && (
                                                            <NPIAutoComplete
                                                                header="Partner"
                                                                name="partner"
                                                                options={partnerOptions}
                                                                placeholder="Select Partner"
                                                            />
                                                        )}
                                                        <Spacer padding={1} />
                                                        {invoiceTo === InvoiceTo.MERCHANT && (
                                                            <MerchantSelectFields
                                                                merchant={values?.merchant}
                                                                merchantAccountNumber={
                                                                    values?.merchantAccountNumber
                                                                }
                                                                merchantOptions={merchantOptions}
                                                                merchantAccountNumberOptions={
                                                                    merchantAccountNumberOptions
                                                                }
                                                                handleChangeMerchant={(value) => {
                                                                    if (value?.value) {
                                                                        const {
                                                                            id,
                                                                            merchantAccountNumber,
                                                                            merchantName,
                                                                            tradingName,
                                                                            merchantContactName,
                                                                        } = selectedMerchant(
                                                                            value?.value
                                                                        );
                                                                        const label =
                                                                            merchantName ??
                                                                            tradingName ??
                                                                            merchantContactName;
                                                                        setFieldValue('merchant', {
                                                                            label,
                                                                            value: id,
                                                                        });
                                                                        setFieldValue(
                                                                            'merchantAccountNumber',
                                                                            {
                                                                                label: merchantAccountNumber,
                                                                                value: id,
                                                                            }
                                                                        );
                                                                    } else {
                                                                        setFieldValue(
                                                                            'merchant',
                                                                            null
                                                                        );
                                                                        setFieldValue(
                                                                            'merchantAccountNumber',
                                                                            null
                                                                        );
                                                                    }
                                                                }}
                                                                handleChangeMerchantAccountNumber={(
                                                                    value
                                                                ) => {
                                                                    if (value?.value) {
                                                                        const {
                                                                            id,
                                                                            merchantAccountNumber,
                                                                            merchantName,
                                                                            tradingName,
                                                                            merchantContactName,
                                                                        } = selectedMerchant(
                                                                            value?.value
                                                                        );
                                                                        const label =
                                                                            merchantName ??
                                                                            tradingName ??
                                                                            merchantContactName;
                                                                        setFieldValue(
                                                                            'merchantAccountNumber',
                                                                            {
                                                                                label: merchantAccountNumber,
                                                                                value: id,
                                                                            }
                                                                        );
                                                                        setFieldValue('merchant', {
                                                                            label,
                                                                            value: id,
                                                                        });
                                                                    } else {
                                                                        setFieldValue(
                                                                            'merchantAccountNumber',
                                                                            null
                                                                        );
                                                                        setFieldValue(
                                                                            'merchant',
                                                                            null
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Box className="mt-20">
                                                        <SelectBaseField
                                                            label="Billing Cycle"
                                                            name="billingCycle"
                                                            inputLabel="Select Billing Cycle"
                                                            setSelectedValue={
                                                                handleOnChangeBillingCycle
                                                            }
                                                            menuItems={[
                                                                {
                                                                    key: 'RECURRING',
                                                                    value: 'Recurring',
                                                                },
                                                                {
                                                                    key: 'ONE_OFF',
                                                                    value: 'One-off',
                                                                },
                                                            ]}
                                                        />
                                                    </Box>
                                                </Stack>
                                            </MainCard>
                                        </Grid>
                                        <Grid item xs={12} md={7}>
                                            <BillingInformation
                                                partner={(partnersData?.partners ?? []).find(
                                                    ({ id }) => {
                                                        return id === values?.partner?.id;
                                                    }
                                                )}
                                                merchant={(
                                                    merchantsData?.merchants?.data ?? []
                                                ).find(({ id }) => {
                                                    return id === values?.merchant?.value;
                                                })}
                                            />
                                            <Box sx={{ marginTop: '20px' }}>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={4}>
                                                        <ThumbnailCard
                                                            label="Total Net Amount"
                                                            amount={convertsCentsToCurrency(
                                                                updatedTotalValues.totalNet
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <ThumbnailCard
                                                            label="Total Vat Amount"
                                                            amount={convertsCentsToCurrency(
                                                                updatedTotalValues.totalVat
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <ThumbnailCard
                                                            label="Total Invoice Amount"
                                                            amount={convertsCentsToCurrency(
                                                                updatedTotalValues.totalAmount
                                                            )}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                                <MainCard>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <h2 className="font-normal mb-0 mt-10">Invoice Items</h2>
                                        <Box>
                                            <IconButton
                                                onClick={handleAddInvoiceItem}
                                                sx={{ color: '#000' }}
                                            >
                                                <AddBoxIcon sx={{ fontSize: '30px' }} />
                                            </IconButton>
                                            {updatedInvoiceItems.length > 1 && (
                                                <IconButton
                                                    onClick={handleRemoveInvoiceItem}
                                                    sx={{ color: '#000' }}
                                                >
                                                    <IndeterminateCheckBoxIcon
                                                        sx={{ fontSize: '30px' }}
                                                    />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{ marginTop: '20px' }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={3}>
                                                <label className="font-bold mb-5">
                                                    Ledger Account
                                                </label>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <label className="font-bold mb-5">
                                                    Description
                                                </label>
                                            </Grid>
                                            <Grid item xs={1}>
                                                <label className="font-bold mb-5">Quantity</label>
                                            </Grid>
                                            <Grid item xs={1}>
                                                <label className="font-bold mb-5">Net Amount</label>
                                            </Grid>
                                            <Grid item xs={1}>
                                                <label className="font-bold mb-5">Total Net</label>
                                            </Grid>
                                            <Grid item xs={1}>
                                                <label className="font-bold mb-5">Total Vat</label>
                                            </Grid>
                                            <Grid item xs={1}>
                                                <label className="font-bold mb-5">Line Total</label>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    {updatedInvoiceItems.map((item, index: number) => {
                                        return (
                                            <Box sx={{ marginTop: '20px' }} key={index}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={3}>
                                                        <Stack>
                                                            <SelectBase
                                                                name={`ledgerAccount-${index}`}
                                                                value={item.ledgerAccount || ''}
                                                                menuItems={ledgerAccounts.map(
                                                                    ({ value, label }) => {
                                                                        return {
                                                                            key: value,
                                                                            value: label,
                                                                        };
                                                                    }
                                                                )}
                                                                inputLabel="Ledger Account"
                                                                onChange={handleOnchangeInvoiceItem}
                                                            />
                                                        </Stack>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Stack>
                                                            <InputBase
                                                                onChange={handleOnchangeInvoiceItem}
                                                                className={`form-input ${
                                                                    errorMessage.index === index
                                                                        ? 'input-error'
                                                                        : ''
                                                                }`}
                                                                placeholder="Enter Description"
                                                                name={`description-${index}`}
                                                                type="text"
                                                            />
                                                        </Stack>
                                                    </Grid>
                                                    <Grid item xs={1}>
                                                        <Stack>
                                                            <InputBase
                                                                onChange={handleOnchangeInvoiceItem}
                                                                className={`form-input ${
                                                                    errorMessage.index === index
                                                                        ? 'input-error'
                                                                        : ''
                                                                }`}
                                                                type="number"
                                                                name={`quantity-${index}`}
                                                            />
                                                        </Stack>
                                                    </Grid>
                                                    <Grid item xs={1}>
                                                        <Stack>
                                                            <NPICurrencyInput
                                                                name={`netAmount-${index}`}
                                                                handleValue={(valueInCents) => {
                                                                    handleNetAmount(
                                                                        `netAmount`,
                                                                        index,
                                                                        valueInCents,
                                                                        updatedInvoiceItems
                                                                    );
                                                                }}
                                                                className={`form-input ${
                                                                    errorMessage.index === index
                                                                        ? 'input-error'
                                                                        : ''
                                                                }`}
                                                            />
                                                        </Stack>
                                                    </Grid>
                                                    <Grid item xs={1}>
                                                        <Stack>
                                                            <NPICurrencyInput
                                                                name={`totalNet-${index}`}
                                                                /**
                                                                 * (H) TODO:  prop isValueCents boolean to convert internally rather...?
                                                                 * We have defaultValueInCents but that only set the initial value not controlled value
                                                                 * */
                                                                value={convertsCentsToCurrency(
                                                                    item.totalNet
                                                                )}
                                                                disabled
                                                                className="form-input"
                                                            />
                                                        </Stack>
                                                    </Grid>
                                                    <Grid item xs={1}>
                                                        <Stack>
                                                            <NPICurrencyInput
                                                                name={`totalVat-${index}`}
                                                                value={convertsCentsToCurrency(
                                                                    item.totalVat
                                                                )}
                                                                disabled
                                                                className="form-input"
                                                            />
                                                        </Stack>
                                                    </Grid>
                                                    <Grid item xs={1}>
                                                        <Stack>
                                                            <NPICurrencyInput
                                                                name={`lineTotal-${index}`}
                                                                value={convertsCentsToCurrency(
                                                                    item.lineTotal
                                                                )}
                                                                disabled
                                                                className="form-input"
                                                            />
                                                        </Stack>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        );
                                    })}
                                    <Box>
                                        <Typography
                                            className="error mt-5"
                                            data-testid="invoice-items-error"
                                        >
                                            {errorMessage.invoiceItems}
                                        </Typography>
                                    </Box>
                                </MainCard>
                                {showRecurringInvoiceConfig && (
                                    <MainCard>
                                        <h2 className="font-normal mb-0 mt-10">
                                            Recurring Invoice Configuration
                                        </h2>
                                        <Stack spacing={5} className="mt-20">
                                            <Box>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <SelectBaseField
                                                            label="Invoice Occurrence"
                                                            name="invoiceOccurrence"
                                                            inputLabel="Select Invoice Occurrence"
                                                            setSelectedValue={
                                                                handleOnChangeInvoiceOccurrence
                                                            }
                                                            menuItems={[
                                                                { key: 'DAILY', value: 'Daily' },
                                                                { key: 'WEEKLY', value: 'Weekly' },
                                                                {
                                                                    key: 'MONTHLY',
                                                                    value: 'Monthly',
                                                                },
                                                            ]}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <NPIAutoComplete
                                                            header="Invoice Frequency"
                                                            name="invoiceFrequency"
                                                            options={invoiceFrequencyOptions}
                                                            placeholder="Select Invoice Frequency"
                                                            setSelectedValue={
                                                                handleOnchangeInvoiceFrequency
                                                            }
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                            <Box>
                                                <Grid container spacing={3}>
                                                    <Grid item xs={12} md={6}>
                                                        <SelectBaseField
                                                            label="Occurrence Type"
                                                            name="occurrenceType"
                                                            inputLabel="Select Occurrence Type"
                                                            setSelectedValue={
                                                                handleOnChangeOccurenceType
                                                            }
                                                            menuItems={[
                                                                {
                                                                    key: 'FIXED_OCCURRENCE',
                                                                    value: 'Fixed Occurence',
                                                                },
                                                                {
                                                                    key: 'CREATE_UNTIL_CANCEL',
                                                                    value: 'Create Until Cancel',
                                                                },
                                                            ]}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <Stack>
                                                            <label className="font-bold mb-5">
                                                                Recurring Start Date
                                                            </label>
                                                            <NPISingleDatePicker
                                                                value={recurringStartDate}
                                                                onChange={handleRecurringStartDate}
                                                                minDate={invoiceDate}
                                                            />
                                                        </Stack>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                            <Box>
                                                <Grid container spacing={3}>
                                                    {showNumberOfInvoices && (
                                                        <Grid item xs={12} md={6}>
                                                            <InputBaseField
                                                                name="numberOfInvoices"
                                                                label="Number of Invoices"
                                                                placeholder="Enter Number of invoices"
                                                                type="number"
                                                            />
                                                        </Grid>
                                                    )}
                                                </Grid>
                                            </Box>
                                        </Stack>
                                    </MainCard>
                                )}
                                <Box>
                                    <div className="flex space-end">
                                        <Button
                                            className="reset-btn btn-medium mr-20"
                                            onClick={() => {
                                                return navigate('/finance/sales-invoices');
                                            }}
                                            variant="contained"
                                            size="medium"
                                            disableElevation
                                        >
                                            Cancel
                                        </Button>
                                        {isLoading || isSubmitting ? (
                                            <LoadingButton
                                                className="btn-medium"
                                                style={{
                                                    backgroundColor: '#F18F34',
                                                    color: '#fff',
                                                    padding: '5px 30px 5px 10px',
                                                }}
                                                isLoading
                                                loadingPosition="start"
                                                startIcon={<SaveIcon />}
                                                variant="contained"
                                            >
                                                Save
                                            </LoadingButton>
                                        ) : (
                                            <Box className="btn-wrapper" sx={{ cursor: 'pointer' }}>
                                                <Button
                                                    className={`${
                                                        isValid ? 'btn-primary' : 'btn-dark'
                                                    } btn-medium`}
                                                    disabled={!isValid || !dirty}
                                                    type="submit"
                                                    variant="contained"
                                                    size="medium"
                                                    disableElevation
                                                >
                                                    Save
                                                </Button>
                                            </Box>
                                        )}
                                    </div>
                                </Box>
                            </Stack>
                        </Form>
                    );
                }}
            </Formik>
        </PageContent>
    );
};

export default AddSalesInvoicePage;
